import {
  PropsWithChildren,
  JSX,
  createElement,
  MouseEvent as ReactMouseEvent, MouseEventHandler
} from 'react';
import classNames from 'classnames';
import { Link, LinkProps } from 'react-router-dom';

export type ButtonView = 'main' | 'secondary' | 'link' | 'dark' | 'attention' | 'light'
type TButtonType = 'button' | 'a' | 'Link'

interface TElement {
  'a': HTMLAnchorElement,
  'button': HTMLButtonElement
  'Link': HTMLAnchorElement
}

type THandler<T extends TButtonType> = (e: ReactMouseEvent<TElement[T]>) => void

type TAdditionalFields<T extends TButtonType> = {
  view?: ButtonView
  onClick?: THandler<T>
} & (T extends 'button'
  ? {
    tag?: T
  }
  : {
    tag: T
  })

type TButtonProperties<T extends TButtonType> = T extends Exclude<T, 'Link'>
  ? JSX.IntrinsicElements[T]
  : T extends Extract<T, 'Link'>
    ? LinkProps
    : never

type TButtonProps<T extends TButtonType> = {
  disabled?: boolean
} & TButtonProperties<T> & PropsWithChildren & TAdditionalFields<T>

const isLinkProps = <T extends TButtonType>(params: TButtonProps<T>): params is Extract<TButtonProps<T>, TButtonProps<'Link'>> => {
  return params.tag === 'Link'
}

const getViewClasses = (view: ButtonView) => {
  switch (view) {
    case 'main':
      return 'mainButton'
    case 'secondary':
      return 'secondaryButton'
    case 'dark':
      return 'darkButton'
    case 'attention':
      return 'attentionButton'
    case 'link':
      return 'linkButton'
    case 'light':
      return 'lightButton'
  }
}

const Button = <T extends TButtonType = 'button'>(
  params: TButtonProps<T>) => {
  const {
    view = 'main',
    tag = 'button',
    children,
    onClick,
    disabled,
    ...props
  } = params
  const onClickHandler: THandler<T> = (e) => {
    if (disabled) {
      e.preventDefault()
      return
    }
    onClick?.(e)
  }

  if (isLinkProps(params)) {
    const { ...linkProps } = params
    return <Link
      {...linkProps}
      className={classNames(
        'button',
        getViewClasses(view),
        disabled && 'disabled',
        props?.className
      )}
      onClick={onClickHandler as MouseEventHandler<HTMLAnchorElement>}
    >{children}</Link>
  }

  return createElement(tag, {
    ...props,
    disabled,
    className: classNames(
      'button',
      getViewClasses(view),
      disabled && 'disabled',
      props?.className
    ),
    onClick: onClickHandler
  }, children)
}

export default Button
